/* eslint-disable @typescript-eslint/member-ordering */
import { AccountFacade } from '@mode/accounts/contract';
import { DatasourceFacade } from '@mode/accounts/domain';
import { capraIconBuildingEdit, capraIconBuildingView, capraIconLock } from '@mode/capra';
import { GithubFacade } from '@mode/github/contract';
import {
  AccountResource,
  OrganizationHelpers,
  OrganizationResource,
  ReportResource,
  ReportRunResource,
  SpaceResource,
  StarResource,
} from '@mode/legacy/data-access-model-js';
import { ModeResourceHelper } from '@mode/shared/util-halogen';
import { ReportDbtMetadataFacade, ReportViewerFacade, fromReportViewerSelectors } from '@mode/report-viewer/domain';
import { ReportFacade } from '@mode/reports/domain';
import { PlatformHeaderNavPanelComponentFacade } from './platform-header-nav-panel-component.facade';
import { RunStatus, ShareDialogData, SlackAppDetails } from '@mode/reports/ui';
import {
  DefinitionState,
  MenuOptionId,
  MenuOptionRecord,
  QueryState,
  ReportRunState,
} from '@mode/legacy/types-app-state';
import {
  AvatarTypes,
  BreadcrumbTypes,
  CollectionAccessLevel,
  DisplayMode,
  ReportTypes,
  SpaceType,
} from '@mode/shared/contract-common';
import { DataViewResponseTypes, DatasourceDomain } from '@mode/shared/contract-common';
import { BreadcrumbClickedEvent } from '@mode/shared/ui';
import { getAppName, getCurrentUsername } from '@mode/shared/util-js';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { ToastService } from '@mode/shared/util-toast';
import { DataViewFacade } from '@mode/switch-views/contract';
import type * as ngRouter from '@uirouter/angularjs';
import * as angular from 'angular';
import { Map as ImmMap } from 'immutable';
import { isNil, pick } from 'lodash';
import { BehaviorSubject, Observable, ReplaySubject, Subscription, combineLatest as combine, of } from 'rxjs';
import { combineLatestWith, filter, first, map, mergeMap, startWith, switchMap, take, tap } from 'rxjs/operators';
import { MixableBaseController } from '~/common/component';
import { isDatasetsRoute, isExplorationRoute } from '~/lib/router-helpers';
import { DefinitionsFacade, selectDefinition } from '~/modules/definitions/domain';
import { buildViewDetailsUiSref } from '~/modules/report-viewer/routes/routing-helpers';
import { ExplorationViewDataService } from '~/modules/report-viewer/services/exploration-view-data.service';
import { ActiveExplorationSelectors } from '~/selectors/active-exploration.selectors';
import { ExplorerSelectors } from '~/selectors/explorer.selectors';
import { SelectedReportSelectors } from '~/selectors/selected-report.selectors';
import { ExplorationVizService } from '~/services/exploration/exploration-viz.service';
import { AppStore } from '~/types/main.types';
import { StoreConnector } from '../../common/store-connector';
import { ModeApp } from '../../reducers';
import {
  accountOrganizationsSelector,
  accountPreferenceSelector,
  accountSelector,
  currentUserBelongsToReportContextSelector,
  definitionActionsIndexByIdSelector,
  loggedInStateSelector,
  reportContextAvatarSelector,
  reportContextSelector,
  reportContextWebLinkSelector,
  selectedReportCollectionIsCommunity,
  selectedReportCollectionIsPersonal,
  selectedReportDisplayNameSelector,
  selectedReportPythonNotebookLinkSelector,
  selectedReportReportRunStateSelector,
  selectedReportRunCreatedAtSelector,
  selectedReportSpaceDefaultAccessSelector,
  selectedReportSpaceDisplayNameSelector,
  selectedReportSpaceSelector,
  selectedReportSpaceWebLinkIsViewableSelector,
  selectedReportSpaceWebLinkSelector,
  selectedReportUserStarSelector,
} from '../../reducers/selectors';
import { PlatformHeaderSelectors } from '../../selectors/platform-header.selectors';
import { ClientDataService } from '../../services/client-data-service';
import DatasetService from '../../services/dataset-service';
import DataSourceService from '../../services/data-source.service';
import { DefinitionRunService } from '../../services/definition-run-service';
import { FeatureToggleService } from '../../services/feature-toggle-service';
import { ReportDataService } from '../../services/report-data-service';
import { ReportRunDataService } from '../../services/report-run-service';
import { ReportSaveService } from '../../services/report-save-service';
import { ReportStateService } from '../../services/report-state-service';
import { PlatformHeaderNavMenuType } from '../platform-header-nav/platform-header-nav-controller';
import { Breakpoint } from '../platform-header/platform-header-controller';
import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';
import { PublishOption } from '@mode/dataset/contract';
import { DatasetSelectors } from '@mode/dataset/domain';

export default class PlatformHeaderNavContainerController extends MixableBaseController {
  // bindings
  navDisplayMode: DisplayMode;
  menuType: PlatformHeaderNavMenuType;
  breakpoint: Breakpoint;

  // observables
  account$: Observable<AccountResource>;
  breadcrumbs$: Observable<BreadcrumbTypes.Breadcrumb[]>;
  explorationBreadcrumbs$: Observable<BreadcrumbTypes.Breadcrumb[]>;
  canSyncToGithub$: Observable<boolean>;
  businessUpsellEnabled$: Observable<boolean>;
  collectionBreadcrumb$: Observable<BreadcrumbTypes.Breadcrumb>;
  defaultCommitMessageText$: Observable<string>;
  definitionOptions$: Observable<ImmMap<string, MenuOptionRecord<MenuOptionId.Definition>>>;
  expectedRuntime$: Observable<number>;
  hasUnsyncedChanges$: Observable<boolean>;
  isExplorationOwner$: Observable<any>;
  allowedToExplore$: Observable<boolean>;
  lastSuccessfulRunToken$: Observable<string>;
  lastSyncedAt$: Observable<Readonly<Date>>;
  menuOptions$: Observable<MenuOptionRecord<MenuOptionId.AllTypes>>;
  modeStudioEnabled$: Observable<boolean>;
  orgContext$: Observable<any>;
  orgs$: Observable<OrganizationResource[]>;
  report$: Observable<ReportResource & { _resource?: ReportResource }>;
  reportBreadcrumb$: Observable<BreadcrumbTypes.Breadcrumb>;
  reportIsExternal$: Observable<boolean>; // Map<string, MenuOptionRecord<MenuOptionId.Report>>
  reportOptions$: Observable<ImmMap<MenuOptionId.Report, MenuOptionRecord<MenuOptionId.Report>>>;
  reportPythonNotebookAvailable$: Observable<string>;
  reportRun$: Observable<ReportRunResource>;
  reportRunAt$: Observable<Date>;
  reportRunsLink$: Observable<string>;
  reportSpace$: Observable<SpaceResource>;
  reportUserStar$: Observable<StarResource>;
  shareDialogData$: Observable<ShareDialogData>;
  selectedDefinition$: Observable<DefinitionState>;
  sharingBannerHidden$: Observable<boolean>;
  showShareButton$: Observable<boolean>;
  runViewIsRecent$: Observable<boolean>;
  activeExplorationQueryToken$: Observable<string>;
  reportDbtData$: Observable<ReportTypes.ReportDbtMetaData>;
  reportDbtDataLoading$: Observable<boolean>;
  referencedDatasources$: Observable<DatasourceDomain.Datasource[]>;
  editAccess: false;
  queries$: Observable<ReadonlyArray<QueryState>>;
  collectionUrl$: Observable<string> = this.store.select(selectedReportSpaceWebLinkSelector);
  reportRunStatus$: Observable<RunStatus>;
  saveExplorationToolTipCopy: string;
  owner$: BehaviorSubject<OrganizationResource> | undefined = new BehaviorSubject(undefined);
  reportOwnerWithSpaces$: Observable<any>;
  isDatasetPublished$ = this.platFormHeaderNavComponentFacade.isDatasetPublished$;
  datasetPublication$ = this.platFormHeaderNavComponentFacade.datasetThoughtspotPublication$;
  datasetQueryRunSucceeded$ = this.platFormHeaderNavComponentFacade.datasetQueryRunSucceeded$;
  publishOptionCTA$ = this.platFormHeaderNavComponentFacade.publishOptionCTA$;
  isReportArchived$ = new BehaviorSubject(false);

  // temp feature flags
  reportStateEnabled$: Observable<boolean>;
  datasetRepublishing$: Observable<boolean>;
  canDatasetRepublish$: Observable<boolean>;
  canPublishDataset$ = this.platFormHeaderNavComponentFacade.canPublishDataset$;
  isAnalystStudio$: Observable<boolean>;

  // subscriptions
  avatarSubscription: Subscription;
  lastSuccessfulRunTokenSubscription: Subscription;
  reportRunsLinkSubscription: Subscription;
  spaceLinkSubscription: Subscription;
  starOptionSubscription: Subscription;
  currentExplorationNameSubscription: Subscription;
  currentExplorationName: string;
  currentExplorationDescriptionSubscription: Subscription;
  currentExplorationDescription: string;
  thoughtspotDataWorkspaceLinkSubscription: Subscription;
  thoughtspotDataWorkspaceLink: string;

  // callbacks
  onSimpleShare?: () => void;

  // local state
  _lastSuccessfulRunToken: string;
  _mobileMenuIsOpen = false;
  _savedNotebook: any;
  _reportRunsLink: string;
  _spaceLink: string;
  avatar: AvatarTypes.ReportContextAvatar;
  inMemoryExploration: DataViewResponseTypes.Exploration;
  shareLink: string;
  starOption: MenuOptionRecord<MenuOptionId.AllTypes>;
  datasetLastRefreshed$: Observable<string>;
  runStatus: RunStatus;
  isPersonal: boolean;
  owner: OrganizationResource;
  trialLink: string;
  readonly appName = getAppName();

  static $inject = [
    '$state',
    '$transitions',
    '$timeout',
    '$window',
    '$log',
    'AccountModel',
    'DataSourceModel',
    'OrgModel',
    'ReportModel',
    'ReportRunModel',
    'SpaceModel',
    'RxStoreService',
    'StoreService',
    'ClientDataService',
    'DefinitionRunDataService',
    'DataSourceService',
    'DatasetService',
    'FeatureToggleService',
    'ReportFacade',
    'ReportDataService',
    'ReportRunDataService',
    'ReportSaveService',
    'ReportStateService',
    'ExplorationViewDataService',
    'ExplorationVizService',
    'DatasourceFacade',
    'ToastService',
    'FeatureFlagsFacade',
    'ThoughtspotIntegrationService',
    'DataViewFacade',
    'ModeCollection',
    'ReportDbtMetadataFacade',
    'ModeResource',
    'ObservabilityService',
    'AccountFacade',
    'GithubFacade',
    'DefinitionsFacade',
    'PlatformHeaderNavPanelComponentFacade',
    'ReportViewerFacade',
  ];

  constructor(
    private $state: ngRouter.StateService,
    private $transitions: ngRouter.Transition,
    private $timeout: angular.ITimeoutService,
    private $window: angular.IWindowService,
    private $log: angular.ILogService,
    private AccountModel: any,
    private DataSourceModel: any,
    private OrgModel: any,
    private ReportModel: any,
    private ReportRunModel: any,
    private SpaceModel: any,
    private store: AppStore,
    private storeLegacy: StoreConnector<ModeApp>,
    private clientDataService: ClientDataService,
    private DefinitionRunDataService: DefinitionRunService,
    private dataSourceService: DataSourceService,
    private datasetService: DatasetService,
    private featureToggleService: FeatureToggleService,
    private reportFacade: ReportFacade,
    private reportDataService: ReportDataService,
    private reportRunDataService: ReportRunDataService,
    private reportSaveService: ReportSaveService,
    private reportStateService: ReportStateService,
    private explorationViewDataService: ExplorationViewDataService,
    private explorationVizService: ExplorationVizService,
    private datasourceFacade: DatasourceFacade,
    private alertService: ToastService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService,
    private dataViewFacade: DataViewFacade,
    private ModeCollection,
    private dbtMetadataFacade: ReportDbtMetadataFacade,
    private ModeResource: any,
    private observabilityService: ObservabilityService,
    private accountFacade: AccountFacade,
    private githubFacade: GithubFacade,
    private definitionsFacade: DefinitionsFacade,
    private platFormHeaderNavComponentFacade: PlatformHeaderNavPanelComponentFacade,
    private reportViewerFacade: ReportViewerFacade
  ) {
    super();
    this.datasourceFacade.loadReportDatasources();
    this.saveExplorationToolTipCopy = `This Exploration uses a Dataset <br/> as its source data. Saving is <br/> currently unsupported.`;
    this.businessUpsellEnabled$ = this.featureToggleService.featureBusinessUpsellEnabled$;
    this.modeStudioEnabled$ = this.featureToggleService.featureModeStudioEnabled$;
    this.hasUnsyncedChanges$ = this.githubFacade.hasUnsyncedChanges$;
    this.lastSyncedAt$ = this.githubFacade.lastSynchedAt$;
    this.canSyncToGithub$ = this.githubFacade.canSyncToGithub$;
    this.isAnalystStudio$ = this.thoughtspotIntegrationService.currOrgIntegrated$;

    this.report$ = this.getHeaderReportData().pipe(
      tap(() =>
        this.observabilityService.recordSpanTiming(
          ObservabilityEvents.HEADER_REPORT_LOADED,
          ObservabilityEvents.HEADER_LOADING
        )
      )
    );

    this.reportRunsLink$ = this.report$.pipe(map((data) => data?._resource?._links?.['web_report_runs']?.href));
    this.lastSuccessfulRunToken$ = this.report$.pipe(map((reportData) => reportData?.last_successful_run_token));

    this.reportStateEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.SkipReportAccountResolver);
    this.datasetRepublishing$ = this.featureFlagsFacade.asObservable(FeatureFlag.DatasetRepublishing);
    this.canDatasetRepublish$ = this.datasetRepublishing$.pipe(
      map((isEnabled) => isEnabled),
      combineLatestWith(this.isDatasetPublished$),
      map(([isEnabled, isPublished]) => isEnabled && isPublished)
    );

    this.account$ = this.store.select(accountSelector);

    this.orgs$ = this.store
      .select(accountOrganizationsSelector)
      .pipe(map((resources) => ModeResourceHelper.resourcesToModels(resources, this.OrgModel.model)));

    this.avatarSubscription = this.store
      .select(reportContextAvatarSelector)
      .subscribe((avatar) => (this.avatar = avatar));

    this.orgContext$ = this.store
      .select(reportContextSelector)
      .pipe(map((resource) => ModeResourceHelper.resourceToModel(resource, this.AccountModel.model)));

    this.isExplorationOwner$ = this.store
      .select(ActiveExplorationSelectors.getExplorationOwnerUserName)
      .pipe(map((owner) => owner === getCurrentUsername(this.$window.document)));

    this.activeExplorationQueryToken$ = this.store.select(ActiveExplorationSelectors.getQueryToken);

    this.expectedRuntime$ = this.report$.pipe(map((data) => data?._resource?.expected_runtime));

    this.allowedToExplore$ = this.featureToggleService.featureExploreEnabled$;

    this.sharingBannerHidden$ = this.store.select(accountPreferenceSelector).pipe(
      filter((preferenceResource) => !!preferenceResource),
      map((preferenceResource) => preferenceResource.hidden_banners.external_sharing)
    );

    this.reportIsExternal$ = this.report$.pipe(
      filter((reportResource) => !!reportResource),
      map((reportResource) => reportResource.shared || reportResource.is_embedded)
    );

    this.reportOptions$ = this.store.select(PlatformHeaderSelectors.menuOptionIndexByIdSelector);

    this.reportRun$ = this.store
      .select(selectedReportReportRunStateSelector)
      .pipe(
        map((reportRunState: ReportRunState) =>
          ModeResourceHelper.resourceToModel(reportRunState && reportRunState.resource, this.ReportRunModel.model)
        )
      );

    this.runStatus = {
      completed: false,
      exportableDatasets: false,
    };

    this.reportRunStatus$ = this.reportRunDataService.selectedQueryReportRunState$.pipe(
      map((state) => {
        return this.setReportState(state);
      })
    );

    this.shareDialogData$ = combine([this.report$, this.reportRun$, this.reportRunStatus$, this.orgContext$]).pipe(
      map(([report, reportRun, reportRunStatus, orgContext]) => {
        const reportResource: ReportResource = (report as any)?._resource;
        const spaceType = reportResource?._embedded.space?.space_type;
        const reportRunEmailModel = (reportRun as any).modelFromForm_reportRunEmails();
        return {
          reportShared: report.shared,
          reportRunStatus,
          emailAttachmentsDisabled: !orgContext._resource.settings.email_attachments_enabled,
          externalSharingDisabled:
            !(report.shared
              ? !!reportResource?._forms.disable_external_sharing
              : !!reportResource?._forms.enable_external_sharing) && this.editAccess,
          externalSharingTooltip:
            spaceType === SpaceType.private && this.editAccess
              ? 'To use External Sharing, move this Report to a Workspace Collection'
              : 'You must be an Editor to update External Sharing',
          schedulingEnabled: (report as any)?.canCreateFromForm_reportSchedules?.() ?? false,
          internalLiveReportLink: report.link,
          internalStaticReportLink: reportRun.shareLink,
          externalLiveReportLink: report.externalLink,
          externalStaticReportLink: reportRun.externalLink,
          ownerUsername: orgContext.username,
          ownerMembershipType: orgContext.membershipType,
          emailFormModel: {
            message: reportRunEmailModel.message ?? '',
            options: this.createEmailOptions(reportRunEmailModel),
          },
          slackAppDetails$: this.initSlackApp(orgContext),
          validEmailDomains: orgContext.authorized_domains as string[],
        };
      })
    );

    this.reportRunAt$ = this.store.select(selectedReportRunCreatedAtSelector);

    this.reportSpace$ = this.store
      .select(selectedReportSpaceSelector)
      .pipe(map((resource) => ModeResourceHelper.resourceToModel(resource, this.SpaceModel.model)));

    this.selectedDefinition$ = this.DefinitionRunDataService.definitionRunState$.pipe(
      map((definitionRunState) => definitionRunState.definition)
    );

    this.reportDbtData$ = this.dbtMetadataFacade.reportDbtMetadata$;
    this.reportDbtDataLoading$ = this.dbtMetadataFacade.reportDbtMetadataLoading$;

    this.definitionOptions$ = this.store.select(definitionActionsIndexByIdSelector);
    this.reportPythonNotebookAvailable$ = this.store.select(selectedReportPythonNotebookLinkSelector).pipe(
      filter((link) => !isNil(link)),
      take(1)
    );

    this.menuOptions$ = this.reportOptions$.pipe(
      combineLatestWith(this.definitionOptions$),
      map(([reportOptions, definitionOptions]) => {
        switch (this.menuType) {
          case PlatformHeaderNavMenuType.Report:
            return reportOptions;
          case PlatformHeaderNavMenuType.DefinitionEditor:
            return definitionOptions;
          default:
            return reportOptions;
        }
      }),
      map(this.shamefullyPatchOptionsStream.bind(this))
    );

    this.shareLink = this.$state.href(
      'explorationShow.focus',
      { explorationId: this.$state.params.explorationId },
      { absolute: true }
    );

    this.defaultCommitMessageText$ = this.report$.pipe(
      filter((r) => !!r),
      combineLatestWith(this.DefinitionRunDataService.definitionRunState$),
      map(([reportResource, definitionRun]) => {
        switch (this.menuType) {
          case PlatformHeaderNavMenuType.Editor:
            return `Update Report '${reportResource.name || reportResource.token}'`;
          case PlatformHeaderNavMenuType.DefinitionEditor:
            return `Update Definition '${definitionRun.definition.name}'`;
        }
      })
    );

    this.collectionBreadcrumb$ = this.featureFlagsFacade.asObservable(FeatureFlag.SkipReportAccountResolver).pipe(
      switchMap((useFacade) =>
        useFacade && this.isReportEditor
          ? this.reportFacade.displayName$
          : this.store.select(selectedReportDisplayNameSelector)
      ),
      combineLatestWith(
        this.store.select(selectedReportSpaceDisplayNameSelector),
        this.store.select(selectedReportSpaceWebLinkSelector),
        this.store.select(selectedReportSpaceWebLinkIsViewableSelector),
        this.store.select(selectedReportSpaceDefaultAccessSelector),
        this.store.select(selectedReportCollectionIsCommunity),
        this.store.select(selectedReportCollectionIsPersonal)
      ),
      map(([reportName, name, webLink, isVisible, defaultAccess, isCommunity, isPersonal]) =>
        this.getCollectionBreadcrumb(name, webLink, isVisible, defaultAccess, isCommunity, isPersonal)
      )
    );

    this.reportBreadcrumb$ = this.featureFlagsFacade.asObservable(FeatureFlag.SkipReportAccountResolver).pipe(
      switchMap((useFacade) =>
        useFacade && this.isReportEditor
          ? this.reportFacade.displayName$
          : this.store.select(selectedReportDisplayNameSelector)
      ),
      combineLatestWith(this.report$),
      map(([reportName, report]) => {
        const isReportArchived = report?.archived;
        this.isReportArchived$.next(isReportArchived);
        return this.getReportBreadcrumb(reportName, this.$state.params);
      })
    );

    this.breadcrumbs$ = this.featureFlagsFacade.asObservable(FeatureFlag.SkipReportAccountResolver).pipe(
      switchMap((useFacade) =>
        useFacade && this.isReportEditor ? this.reportFacade.displayName$ : this.reportFacade.displayNameForHeader$
      ),
      combineLatestWith(
        this.DefinitionRunDataService.definitionRunState$,
        this.datasourceFacade.datasources$.pipe(startWith([] as readonly DatasourceDomain.Datasource[])), // start with empty so the breadcrumbs can load wherever we don't need the datasources
        this.orgContext$,
        this.store.select(ExplorerSelectors.title),
        this.collectionBreadcrumb$,
        this.reportBreadcrumb$
      ),
      map(
        ([
          reportName,
          definitionRunState,
          datasources,
          orgContext,
          chartTitle,
          collectionBreadcrumb,
          reportBreadcrumb,
        ]) => {
          const definition = definitionRunState.definition;
          switch (this.menuType) {
            case PlatformHeaderNavMenuType.DefinitionEditor:
              // TODO: Ideally, the definition datasource would be an observable somewhere
              const datasource =
                definition != null ? datasources.find((ds) => ds.id === definition.dataSourceId) : undefined;

              return [
                {
                  type: BreadcrumbTypes.BreadcrumbType.Domain,
                  name: datasource?.displayName || 'Private',
                  href: datasource?.links.home || orgContext.publicDSLink,
                  self: true,
                },
                { type: BreadcrumbTypes.BreadcrumbType.Page, name: definition?.name || 'Untitled Definition' },
              ] as BreadcrumbTypes.Breadcrumb[];
            case PlatformHeaderNavMenuType.Dataset:
              return [
                collectionBreadcrumb,
                {
                  type: BreadcrumbTypes.BreadcrumbType.Page,
                  icon: 'icon-dataset',
                  name: reportName || 'Untitled Dataset',
                  href: null,
                  self: false,
                },
              ] as BreadcrumbTypes.Breadcrumb[];
            case PlatformHeaderNavMenuType.Editor:
            // fallthrough
            case PlatformHeaderNavMenuType.Report:
              return [
                collectionBreadcrumb,
                reportBreadcrumb,
                ...(chartTitle
                  ? [{ type: BreadcrumbTypes.BreadcrumbType.Page, name: chartTitle, href: null, self: false }]
                  : []),
              ] as BreadcrumbTypes.Breadcrumb[];
          }
        }
      ),
      combineLatestWith(
        this.store.select(reportContextSelector),
        this.store.select(reportContextWebLinkSelector),
        this.store.select(loggedInStateSelector),
        this.report$,
        this.store.select(currentUserBelongsToReportContextSelector)
      ),
      map(([breadcrumbs, reportContext, orgWebLink, isLoggedIn, report, belongsToReportContext]) => {
        if (!isLoggedIn) {
          breadcrumbs = breadcrumbs.filter((breadcrumb) => breadcrumb.type !== BreadcrumbTypes.BreadcrumbType.Domain);
        }
        if (this.menuType === PlatformHeaderNavMenuType.Report) {
          // only add initial org breadcrumb if report is owned by an org and user is not logged in
          if (reportContext && !reportContext.user && !isLoggedIn) {
            return [
              { type: BreadcrumbTypes.BreadcrumbType.Org, name: reportContext.name, href: '/', self: true },
              ...breadcrumbs,
            ];
          } else if (reportContext && !reportContext.user && report?.public) {
            // or add initial org breadcrumb if report is a community report owned by an org
            return [
              {
                type: BreadcrumbTypes.BreadcrumbType.Org,
                name: reportContext.name,
                href: belongsToReportContext ? orgWebLink : null,
                self: true,
              },
              ...breadcrumbs,
            ];
          }
        }

        return breadcrumbs;
      })
    );

    this.explorationBreadcrumbs$ = this.createExplorationBreadcrumbs();

    this.reportRunsLinkSubscription = this.reportRunsLink$.subscribe((url) => {
      this._reportRunsLink = url;
    });

    this.spaceLinkSubscription = this.store.select(selectedReportSpaceWebLinkSelector).subscribe((url) => {
      this._spaceLink = url;
    });

    this.lastSuccessfulRunTokenSubscription = this.lastSuccessfulRunToken$.subscribe((token) => {
      this._lastSuccessfulRunToken = token;
    });

    this.reportUserStar$ = this.store.select(selectedReportUserStarSelector);

    this.thoughtspotDataWorkspaceLinkSubscription = this.store
      .select(DatasetSelectors.selectDatasetPublicationDetails)
      .subscribe((publicationDetails) => {
        this.thoughtspotDataWorkspaceLink = publicationDetails?.publishUrl;
      });
    this.currentExplorationNameSubscription = this.store
      .select(ActiveExplorationSelectors.getTitle)
      .subscribe((currentExplorationName) => {
        this.currentExplorationName = currentExplorationName;
      });

    this.currentExplorationDescriptionSubscription = this.store
      .select(ActiveExplorationSelectors.getDescription)
      .subscribe((currentExplorationDescription) => {
        this.currentExplorationDescription = currentExplorationDescription;
      });

    // check if a runId is present in the url and if so that it is the most recent successful runId
    this.runViewIsRecent$ = this.store
      .select(SelectedReportSelectors.getRecentReportRunToken)
      .pipe(
        map(
          (recentReportRunToken) =>
            angular.isUndefined(this.$state.params.runId) ||
            (angular.isDefined(this.$state.params.runId) && this.$window.location.href.includes(recentReportRunToken))
        )
      );

    this.starOptionSubscription = this.reportOptions$
      .pipe(
        map((reportOptions) => {
          switch (this.menuType) {
            case PlatformHeaderNavMenuType.Report:
              return reportOptions.get(MenuOptionId.Report.StarReport);
            default:
              return null;
          }
        })
      )
      .subscribe((starOptionRecord) => {
        this.starOption = starOptionRecord;
      });

    this.queries$ = this.store.select(SelectedReportSelectors.getQueries).pipe(map((queries) => queries.getRecords()));

    this.referencedDatasources$ = combine([
      this.datasourceFacade.reportDatasources$,
      this.queries$.pipe(map((queries) => new Set<number>(queries.map((q) => q.data_source_id as any)))),
    ]).pipe(
      map(([orgDatasources, datasourceIds]) => {
        return orgDatasources.filter((ds) => datasourceIds.has(ds.id));
      })
    );

    this.store
      .select(selectedReportCollectionIsPersonal)
      .subscribe((collectionType) => (this.isPersonal = collectionType));
  }

  $onInit() {
    this.initializeGithubSync();

    this.reportPythonNotebookAvailable$.subscribe(() => {
      this.reportStateService.getNotebook().then((notebook) => {
        this._savedNotebook = notebook;
      });
    });

    this.datasetLastRefreshed$ = this.dataViewFacade.enhancedDatasetResults$.pipe(
      mergeMap((results) =>
        results
          .filter((result) => !result.isLocal)
          .map((dataset) => new Date(dataset.dataRefreshAvailableAsOf).getTime())
          .sort()
      ),
      map((lastRefreshedDataset) => new Date(lastRefreshedDataset).toISOString()),
      first()
    );

    this.reportOwnerWithSpaces$ = combine([this.accountFacade.reportOwner$, this.owner$]).pipe(
      map(([reportOwner, owner]) => ({
        ...reportOwner,
        ...pick(owner, ['spaces', 'new_spaces', 'modelFromForm_spaces', '_resource']),
      }))
    );

    this.showShareButton$ = this.store.select(selectedReportCollectionIsPersonal).pipe(
      switchMap((isPersonal) =>
        this.canPublishDataset$.pipe(
          combineLatestWith(this.reportRun$),
          map(([datasetPublishing, reportRun]) => {
            return !datasetPublishing || (reportRun && !isPersonal);
          })
        )
      )
    );
  }

  $onChanges(changed) {
    if (changed.owner) {
      this.trialLink = OrganizationHelpers.getTrialLink(changed.owner.currentValue._resource);
      if (this.owner) {
        this.owner$.next(this.owner);
      }
    }
  }

  $onDestroy() {
    this.avatarSubscription && this.avatarSubscription.unsubscribe();
    this.lastSuccessfulRunTokenSubscription && this.lastSuccessfulRunTokenSubscription.unsubscribe();
    this.reportRunsLinkSubscription && this.reportRunsLinkSubscription.unsubscribe();
    this.starOptionSubscription && this.starOptionSubscription.unsubscribe();
    this.currentExplorationNameSubscription && this.currentExplorationNameSubscription.unsubscribe();
    this.currentExplorationDescriptionSubscription && this.currentExplorationDescriptionSubscription.unsubscribe();
    this.thoughtspotDataWorkspaceLinkSubscription && this.thoughtspotDataWorkspaceLinkSubscription.unsubscribe();
  }

  initializeGithubSync() {
    switch (this.menuType) {
      case PlatformHeaderNavMenuType.Dataset:
        this.githubFacade.dispatch(GithubFacade.Actions.datasetEditorHeaderGithubSyncInitialized());
        break;
      case PlatformHeaderNavMenuType.Editor:
        this.githubFacade.dispatch(GithubFacade.Actions.reportEditorHeaderGithubSyncInitialized());
        break;
      case PlatformHeaderNavMenuType.DefinitionEditor:
        this.githubFacade.dispatch(
          GithubFacade.Actions.definitionEditorHeaderGithubSyncInitialized({
            context$: this.definitionsFacade.githubContext$,
          })
        );
        break;
      case PlatformHeaderNavMenuType.Report: // github sync info is never shown in viewer
      default:
        break;
    }
  }

  createNewReportWithDataset() {
    this.platFormHeaderNavComponentFacade.createNewReportWithDataset();
  }

  getHeaderReportData(): Observable<ReportResource> {
    return this.reportFacade.headerReportData$.pipe(
      map((resource) => {
        let reportData: ReportResource;
        if (!!resource) {
          reportData = ModeResourceHelper.resourceToModel(new this.ModeResource(resource), this.ReportModel.model);
          this.ReportModel.putLocal(reportData);
        }
        return reportData;
      })
    );
  }

  handleExternalBadgeClick($event) {
    this.clientDataService.updateAccountPreference({
      hidden_banners: {
        external_sharing: false,
      },
    });
  }

  handleBreadcrumbClick({ $event, breadcrumbType }: BreadcrumbClickedEvent): void {
    if (this.menuType === PlatformHeaderNavMenuType.Editor && breadcrumbType === BreadcrumbTypes.BreadcrumbType.Page) {
      $event.preventDefault();
    }

    if (this.hasMobileMenu(breadcrumbType)) {
      $event.preventDefault();
      this.toggleMobileMenu();
    }
  }

  toggleMobileMenu(): void {
    this.$timeout(() => {
      this._mobileMenuIsOpen = !this._mobileMenuIsOpen;
    });
  }

  toggleReportUserStar(): void {
    this.reportDataService.toggleReportUserStar();
  }

  handleMobileMenuToggle($isOpen): void {
    this._mobileMenuIsOpen = $isOpen;
  }

  copyExploration() {
    this.explorationVizService.copyToVizStorage();
  }
  /**
   * Copy success.
   */
  copySuccess() {
    this.alertService.success('Link to Exploration copied successfully');
  }

  copyDatasetSuccess() {
    this.alertService.success('Link to Dataset copied successfully');
  }

  private hasMobileMenu($type: BreadcrumbTypes.BreadcrumbType) {
    return $type === BreadcrumbTypes.BreadcrumbType.Page && this.breakpoint === Breakpoint.Small;
  }

  get mobileMenuIsOpen(): boolean {
    return this._mobileMenuIsOpen;
  }

  get showExploreHeader(): boolean {
    return isExplorationRoute(this.$state);
  }

  get showDatasetsHeader(): boolean {
    return isDatasetsRoute(this.$state);
  }

  get isDefinitionEditor(): boolean {
    return this.menuType === PlatformHeaderNavMenuType.DefinitionEditor;
  }

  get isReportExplore(): boolean {
    return this.$state.includes('**.explore') && this.$state.includes('reportShow');
  }

  get isReportView(): boolean {
    return (
      this.$state.current.name === 'reportShow.noRuns.dashboard' ||
      this.$state.current.name === 'reportShow.runs.dashboard'
    );
  }

  get isReportEditor(): boolean {
    return this.$state.includes('application.reports');
  }

  get isPythonViz(): boolean {
    return (
      this.$state.current.name === 'reportShow.noRuns.pythonviz.view' ||
      this.$state.current.name === 'reportShow.runs.pythonviz.view'
    );
  }

  // Stub for the published state of the dataset
  get isDatasetPublished(): boolean {
    return true;
  }

  get datasetsShareLink(): string {
    return this.$window.location.origin + this.getDatasetPath('edit');
  }

  // Temporary helper to build the appropriate path for share link and redirection. This is necessary until we can either adjust the routing
  //   implementation across editor and viewer to be consistent and account for routes across the other side or decide to just build a service
  //   that implements route building on the FE.
  getDatasetPath(routeType: 'edit' | 'view'): string {
    // Need to fetch differently depending on whether we are in the view or edit context because of router differences
    let org = this.$state.params.ownerId;
    let datasetReportToken = this.$state.params.reportId;
    let path = `/${org}/datasets/${datasetReportToken}`;

    if (routeType === 'edit') {
      path = `/editor${path}`;
    }

    return path;
  }

  navigateToCollection(collectionUrl) {
    this.$window.location.pathname = collectionUrl;
  }

  // Handle navigation to edit and view mode of datasets.
  // TODO: Determine a better way we can abstract this across both the editor and viewer app contexts.
  //   The routers are not set up today to be compatable with each other.
  navigateToDatasetViewPage() {
    this.$window.location.pathname = this.getDatasetPath('view');
  }

  // DatasetReport publish modal
  openDatasetPublishModal(location?: string) {
    this.platFormHeaderNavComponentFacade.openPublishDatasetModal(location);
  }

  openRepublishDatasetModal(location?: string) {
    this.platFormHeaderNavComponentFacade.openRepublishDatasetModal(location);
  }

  publishOptionClicked(option: PublishOption, location?: string) {
    switch (option) {
      case PublishOption.Publish:
        this.platFormHeaderNavComponentFacade.openPublishDatasetModal(location);
        break;
      case PublishOption.Unpublish:
        this.platFormHeaderNavComponentFacade.openUnpublishDatasetModal(location);
        break;
      default:
        break;
    }
  }

  shamefullyPatchOptionsStream(options: ImmMap<string, MenuOptionRecord<MenuOptionId.AllTypes>>) {
    if (this.menuType === PlatformHeaderNavMenuType.Editor) {
      const enableSave = (isEnabled: boolean): boolean => isEnabled && this.reportSaveService.reportHasChanged();
      const overrideSaveOptionEnabledState = (menuOption: MenuOptionRecord<MenuOptionId.Report.Save>) =>
        menuOption.update('enabled', enableSave);

      return options.update(MenuOptionId.Report.Save, overrideSaveOptionEnabledState);
    }

    return options;
  }

  handleDefinitionCreated($redirectLink: string) {
    if ($redirectLink) {
      // state.go doesn't work on urls outside the routes :(
      this.$window.location.href = $redirectLink;
    }
  }

  handleDefinitionUpdate($definitionModel: any) {
    this.storeLegacy.dispatch(selectDefinition($definitionModel));
  }

  handleDefinitionDelete() {
    this.DefinitionRunDataService.deleteSelectedDefinition();
  }

  handleSimpleShare(): void {
    this.onSimpleShare && this.onSimpleShare();
  }

  handleViewDetails(): void {
    const runIdUrlParam = this.$state.params.runId;
    if (runIdUrlParam) {
      this.$state.go('reportShow.runs.detailsView.details', { runId: runIdUrlParam });
    } else {
      this.$state.go('reportShow.noRuns.detailsView.details');
    }
  }

  handleBackToReport(): void {
    this.$transitions.onSuccess({}, () => {
      this.reportViewerFacade.loadReportViewVisualizationsFromExploration(
        this.$state.params.ownerId,
        this.$state.params.reportId
      );
    });
  }

  handleViewReport(): void {
    const runIdUrlParam = this.$state.params.runId;
    if (runIdUrlParam) {
      this.$state.go('reportShow.runs.dashboard', { runId: runIdUrlParam });
    } else {
      this.$state.go('reportShow.noRuns.dashboard');
    }
  }

  /**
   * Build dynamic ui-sref for navigating to the report depending on runId.
   */
  buildUiSref(fragment: string, params: { [key: string]: any } = {}) {
    const hasRunId = typeof this.$state.params.runId === 'string';
    if (hasRunId) {
      params = {
        ...params,
        runId: this.$state.params.runId,
      };
    }
    return buildViewDetailsUiSref(fragment, hasRunId, params);
  }

  handleSave(): void {
    this.reportDataService.updateReportWithAlertAndRefreshPreview();
  }

  handleRunReport(): void {
    //click the run button in params section
    const $parametersRunButton = angular.element('.parameters-run-button button', this.$window.document);

    if (this._reportRunsLink) {
      if ($parametersRunButton.length) {
        this.$timeout(() => {
          $parametersRunButton[0].click();
        });
      } else {
        this.reportRunDataService.runReport(this._lastSuccessfulRunToken, this._reportRunsLink);
      }
    } else {
      this.$log.error('Failed to run report. Run link unavailable');
    }
  }

  trackNewExplorationButtonClicked() {
    const reportId = this.$state.params.reportId;
    this.explorationViewDataService.trackNewExplorationClick(reportId);
  }

  handleReportSpaceTransfer(spaceModel, isViaPublishAction, switchViewToken) {
    this.reportDataService.updateSelectedReportSpace({
      ...spaceModel,
      isViaPublishAction,
      switchViewToken,
    });
  }

  handleDatasetReportSpaceTransfer(spaceModel, isViaPublishAction, switchViewToken) {
    this.reportDataService.updateSelectedReportSpace({
      ...spaceModel,
      isViaPublishAction,
      switchViewToken,
    });
  }
  openSaveExplorationModal() {
    this.explorationViewDataService.openSaveExplorationModal(
      'Save Exploration',
      'An Exploration is your own, personal visualization that doesn’t impact the report. Its data will update automatically to reflect the latest run.',
      'Cancel'
    );
  }

  handleEditExplorationModal() {
    this.explorationVizService.openEditExplorationModal(
      this.currentExplorationName,
      this.currentExplorationDescription
    );
  }

  handleDeleteExplorationModal() {
    const { account, explorationId } = this.$state.params;

    this.explorationVizService.openDeleteExplorationModal(account, explorationId);
  }

  handleReportDeleted($reportToken: string) {
    // redirect to space after delete
    angular.element(this.$window).unbind('beforeunload.report');
    this.$window.location.href = this._spaceLink;
  }

  createExplorationBreadcrumbs(): Observable<BreadcrumbTypes.Breadcrumb[]> {
    if (this.isReportExplore || this.isReportView || this.isPythonViz) {
      return this.store.select(fromReportViewerSelectors.getName).pipe(
        filter((r) => !isNil(r)),
        combineLatestWith(
          this.store.select(fromReportViewerSelectors.getSpaceName),
          this.store.select(fromReportViewerSelectors.getSpaceUrl),
          this.store.select(fromReportViewerSelectors.getIsSpaceViewable),
          this.store.select(fromReportViewerSelectors.getSpaceDefaultAccess),
          this.store.select(fromReportViewerSelectors.getIsCollectionCommunity),
          this.store.select(fromReportViewerSelectors.getIsCollectionPersonal),
          this.store.select(ExplorerSelectors.title)
        ),
        map(
          ([
            reportName,
            collectionName,
            collectionUrl,
            isCollectionViewable,
            collectionDefaultAccess,
            isCollectionCommunity,
            isCollectionPersonal,
            chartTitle,
          ]) => {
            return this.assembleBreadcrumbs(
              collectionName,
              collectionUrl,
              isCollectionViewable,
              collectionDefaultAccess,
              isCollectionCommunity,
              isCollectionPersonal,
              reportName,
              chartTitle
            );
          }
        )
      );
    }
    return this.store.select(ActiveExplorationSelectors.getGfReportDisplayName).pipe(
      filter((r) => !isNil(r)),
      combineLatestWith(
        this.store.select(ActiveExplorationSelectors.getGfSpaceName),
        this.store.select(ActiveExplorationSelectors.getGfSpaceUrl),
        this.store.select(ActiveExplorationSelectors.getGfIsSpaceViewable),
        this.store.select(ActiveExplorationSelectors.getGfSpaceDefaultAccess),
        this.store.select(ActiveExplorationSelectors.getGfIsCollectionCommunity),
        this.store.select(ActiveExplorationSelectors.getGfIsCollectionPersonal),
        this.store.select(ExplorerSelectors.title)
      ),
      map(
        ([
          reportName,
          collectionName,
          collectionUrl,
          isCollectionViewable,
          collectionDefaultAccess,
          isCollectionCommunity,
          isCollectionPersonal,
          chartTitle,
        ]) => {
          return this.assembleBreadcrumbs(
            collectionName,
            collectionUrl,
            isCollectionViewable,
            collectionDefaultAccess,
            isCollectionCommunity,
            isCollectionPersonal,
            reportName,
            chartTitle
          );
        }
      )
    );
  }

  private assembleBreadcrumbs(
    collectionName: string,
    collectionUrl: string,
    isCollectionViewable: boolean,
    collectionDefaultAccess: string,
    isCollectionCommunity: boolean,
    isCollectionPersonal: boolean,
    reportName: string,
    chartTitle: String
  ) {
    let results = [
      this.getCollectionBreadcrumb(
        collectionName,
        collectionUrl,
        isCollectionViewable,
        collectionDefaultAccess,
        isCollectionCommunity,
        isCollectionPersonal
      ),
      this.getReportBreadcrumb(reportName, this.$state.params),
    ];

    if (chartTitle) {
      results.push({
        type: BreadcrumbTypes.BreadcrumbType.Page,
        name: chartTitle as string,
        href: null,
        self: false,
      });
    }

    return results;
  }

  private getCollectionBreadcrumb(
    name: string,
    webLink: string,
    isViewable: boolean,
    defaultAccess: string,
    isCommunity: boolean,
    isPersonal: boolean
  ) {
    const breadcrumb: BreadcrumbTypes.Breadcrumb = {
      type: BreadcrumbTypes.BreadcrumbType.Domain,
      name: name,
      href: (isViewable && webLink) || null,
      self: true,
    };

    if (isCommunity) {
      // show the globe icon instead of the collection icon for community collections
      breadcrumb.icon = 'icon-globe';
    } else if (isPersonal) {
      // show the avatar icon instead of the collection icon for personal collections
      breadcrumb.icon = 'icon-avatar';
    } else {
      // default to the collection icon
      breadcrumb.icon = 'icon-collection';
    }

    breadcrumb.tooltipContent = this.getCollectionBreadcrumbTooltip(isPersonal, isViewable, defaultAccess, name);

    return breadcrumb;
  }

  /**
   * Generate template for collection breadcrumb tooltip
   */
  private getCollectionBreadcrumbTooltip(
    isPersonal: boolean,
    isViewable: boolean,
    defaultAccess: string,
    name: string
  ): string {
    const tooltipIcons = {
      [CollectionAccessLevel.Edit]: capraIconBuildingEdit.data,
      [CollectionAccessLevel.View]: capraIconBuildingView.data,
      [CollectionAccessLevel.Restricted]: capraIconLock.data,
    };

    const tooltipMessages = {
      personal: {
        [CollectionAccessLevel.Edit]: 'All Members can edit content you share from Personal',
        [CollectionAccessLevel.View]: 'All Members can view &amp; explore content you share from Personal',
        [CollectionAccessLevel.Restricted]: 'Only select Members can access content you share from Personal',
      },
      collection: {
        [CollectionAccessLevel.Edit]: 'All Members can edit Reports in this Collection',
        [CollectionAccessLevel.View]: 'All Members can view Reports in this Collection',
        [CollectionAccessLevel.Restricted]: 'Only select Members can access content in this Collection',
      },
    };

    const tooltipTitles = {
      personal: 'Your Personal content',
      collection: `${name} Collection`,
    };

    const defaultAccessTooltip = (icon = '', heading = '', body = '') => `
      <div class="access-tooltip">
        <div>
          <span class="access-tooltip__icon">${icon}</span>
        </div>
        <div class="access-tooltip__desc">
          <strong class="access-tooltip__desc__heading">${heading}</strong>
          <p class="access-tooltip__desc__body">${body}</p>
        </div>
      </div>
    `;

    if (isPersonal && !isViewable) {
      // if the collection is personal and it is not viewable the user doesn't have access
      // add a tooltip explaining why
      return `<div class="access-tooltip-simple">Personal content cannot be visited by other Workspace Members</div>`;
    }

    if (!defaultAccess) {
      return null;
    }

    if (isPersonal) {
      return defaultAccessTooltip(
        tooltipIcons[defaultAccess],
        tooltipTitles.personal,
        tooltipMessages.personal[defaultAccess]
      );
    }

    return defaultAccessTooltip(
      tooltipIcons[defaultAccess],
      tooltipTitles.collection,
      tooltipMessages.collection[defaultAccess]
    );
  }

  private getReportBreadcrumb(reportName, stateParams): BreadcrumbTypes.Breadcrumb {
    return {
      type: BreadcrumbTypes.BreadcrumbType.Page,
      name: reportName,
      href: stateParams.runId
        ? this.$state.href('reportShow.runs.dashboard', {
            account: stateParams.account,
            reportId: stateParams.reportId,
            runId: stateParams.runId,
          })
        : this.$state.href('reportShow.noRuns.dashboard', {
            account: stateParams.account,
            reportId: stateParams.reportId,
          }),
      self: false,
      icon: 'icon-report-builder',
    };
  }

  private createEmailOptions(formModel: Record<string, unknown>): string[] {
    const relevantKeys = [
      'csv_attachments_enabled',
      'data_previews_enabled',
      'data_tables_enabled',
      'pdf_attachments_enabled',
      'report_links_enabled',
    ];
    const options: string[] = [];

    for (const [key, value] of Object.entries(formModel)) {
      if (relevantKeys.includes(key) && value) {
        options.push(key);
      }
    }

    return options;
  }

  private setReportState(state) {
    const newExecutingState = state.reportRun && state.reportRun.running;
    this.runStatus.completed = !newExecutingState;
    if (this.runStatus.completed) {
      this.runStatus.exportableDatasets = this.datasetService.datasetsAreExportable();
    }
    return this.runStatus;
  }

  private getShareHelpText() {
    const helpText = `To share in private channels, the @${this.appName} app must be a member of the channel. \</br\>
    For security, private channels do not appear in the list and must be typed.`;

    return helpText;
  }

  private initSlackApp(orgContext): Observable<SlackAppDetails> {
    const hasSlackApp = orgContext?._resource?.hasLink('slack_app');

    if (!hasSlackApp) {
      return of({
        hasSlackApp: false,
      });
    }

    const slackAppDetails: SlackAppDetails = {
      hasSlackApp,
      slackAppInstaller: '',
      slackAppV2Token: false,
      slackAppUrl: '',
      slackAppTeamName: '',
      slackShareHelpText: '',
      slackChannels: [],
    };

    const slackAppDetails$ = new ReplaySubject<SlackAppDetails>(1);
    orgContext
      .slackApp()
      .then((slackApp) => {
        if (slackApp.state === 'active') {
          slackAppDetails.slackAppInstaller = slackApp.installer_name;
          slackAppDetails.slackAppV2Token = slackApp.version === 'v2';
          slackAppDetails.slackAppUrl = slackApp.team_url;
          slackAppDetails.slackAppTeamName = slackApp.team_name;
          slackAppDetails.slackShareHelpText = this.getShareHelpText();

          slackApp
            .channels()
            .then(this.ModeCollection.elements)
            .then((channels) => {
              slackAppDetails.slackChannels = channels.map(({ id, channelName, name }) => ({ id, channelName, name }));
              slackAppDetails.hasSlackApp = true;
              slackAppDetails$.next(slackAppDetails);
            })
            // problem getting channels
            .catch((e) => {
              slackAppDetails.hasSlackApp = false;
              slackAppDetails$.next(slackAppDetails);
              console.warn('slack channels not found');
            });
        } else {
          slackAppDetails.hasSlackApp = false;
          slackAppDetails$.next(slackAppDetails);
        }
      })
      // problem getting slackApp
      .catch(() => {
        slackAppDetails$.next(null);
        console.error('slack app not found');
      });

    return slackAppDetails$.asObservable();
  }
}
